import { GetStaticProps, InferGetStaticPropsType } from "next";
import { ParsedUrlQuery } from "querystring";

import {
  Button,
  colors,
  HStack,
  NextLink,
  responsive,
  VStack,
  GOI_PLATFORM_URL,
} from "goi_common";

import CLOSED_SANGJO_LIST from "@/constants/closed_sangjo.json";
import ClosedSangjoLayout from "@/containers/closed/ClosedSangjoLayout";
import { Box } from "@chakra-ui/react";

interface Params extends ParsedUrlQuery {
  id: string;
}

export const getStaticPaths = async () => {
  const paths = CLOSED_SANGJO_LIST.map((closedSangjo) => {
    return {
      params: {
        id: String(closedSangjo.id),
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { id } = params as Params;

  const closedSangjoData = CLOSED_SANGJO_LIST.find(
    (closedSangjo) => closedSangjo.id === Number(id)
  );

  const companyName = closedSangjoData?.companyname || "";

  const title = `${companyName} 상조 알아보기 - [똑똑한 소비자를 위한 상조, 고이 프라임]`;
  const description = `${companyName} 상조의 영업 상태와 장례 준비 방안을 자세히 안내합니다. ${companyName} 상조 관련 정보를 한 눈에 확인하세요.`;
  const keywords = `${companyName}, ${companyName} 영업 상태, ${companyName} 폐업, ${companyName} 선수금, ${companyName} 상조 폐업, ${companyName} 상조, ${companyName} 내상조그대로, ${companyName} 상조 상담, 폐업 상조, 상조 선수금 보전, 상조 정보, 폐업 상조 안내, 상조 서비스, 상조 폐업 대처`;

  return {
    props: {
      closedSangjoData,
      pageDescription: description,
      pageOgTitle: title,
      pageOgDescription: description,
      pageKeywords: keywords,
      pageOgImage: "",
    },
  };
};

const ClosedSangjoPage = ({
  closedSangjoData,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { companyname, openDate, closeDate, depositAgency, status } =
    closedSangjoData;

  const closedSangjoRows = [
    { key: "영업상태", value: status },
    { key: "영업개시일", value: openDate },
    { key: "폐업/등록취소일", value: closeDate },
    { key: "선수금 보전기관", value: depositAgency },
  ];

  const isBeforeClosedLawChange = new Date(closeDate) < new Date("2018-08-22");

  return (
    <>
      <ClosedSangjoLayout
        style={responsive({
          padding: { base: "30px 20px", md: "30px 0px 90px" },
        })}
      >
        <h1 className="subtitle_21_b gray900">
          <b className="orange650">{companyname}</b> 바로알기
        </h1>
        <VStack gap="12px" padding="26px 0">
          <h2 className="subtitle_18_b gray900">
            {companyname} 지금 어떤 상태인가요?
          </h2>
          <p className="body_14_m gray900">
            {companyname}는 현재 <b>{status}</b> 상태입니다.
          </p>
          <VStack
            borderRadius="8px"
            padding="10px 30px"
            background={colors.gray50}
            gap="10px"
          >
            {closedSangjoRows.map((res) => (
              <HStack
                className="body_14_m gray900"
                justifyContent="space-between"
                key={res.value}
              >
                <Box textAlign="left" width="50%">
                  {res.key}
                </Box>
                <Box textAlign="left" width="50%">
                  {res.value}
                </Box>
              </HStack>
            ))}
          </VStack>
        </VStack>

        <VStack gap="12px" padding="26px 0">
          <h2 className="subtitle_18_b gray900">
            모르는 업체가 {companyname}를 인수했다며 상조 서비스를 제공해준다고
            합니다.
          </h2>
          <p className="body_14_m gray900">
            {companyname} 폐업 소식만으로도 놀라셨을 텐데, 인수 소식까지 들려와
            더욱 심란하셨을 것 같습니다. 안타깝게도, 이는 거짓일 가능성이
            높습니다. {status} 상태의 상조회사 회원들을 대상으로 한 허위 영업이
            자주 발생하고 있으니 각별히 주의하시길 바랍니다.
          </p>
        </VStack>
        <VStack gap="12px" padding="26px 0">
          <h2 className="subtitle_18_b gray900">
            {companyname} 피해 보상은 어떻게 받나요?
          </h2>
          <p className="body_14_m gray900">
            {companyname} 가입자는 {status}일로부터{" "}
            {isBeforeClosedLawChange ? 2 : 3}년 간 납입액의 50%를{" "}
            {depositAgency}에서 보상받으실 수 있습니다. 불안한 상황에서 작은
            위로가 되길 바랍니다.
          </p>
        </VStack>
        <VStack gap="12px" padding="26px 0">
          <h2 className="subtitle_18_b gray900">
            장례를 합리적으로 치르고 싶은데 어떻게 준비해야 할까요?
          </h2>
          <p className="body_14_m gray900">
            {companyname} {status}(으)로 불안한 마음이 드실 것 같습니다. 더이상
            선불제 상조 가입하지 마시고, 납입액의 50%를 보상받아 후불제 상조로
            장례를 준비하세요. 장례 비용 견적을 확인하며, 편안하고 합리적인
            선택을 하실 수 있도록 도와 드리겠습니다.
          </p>
        </VStack>

        <VStack alignItems="center" width="100%" margin="60px 0 20px">
          <NextLink
            href={`${GOI_PLATFORM_URL}/match/closed-request/`}
            css={{ marginBottom: "28px" }}
            event={{ newGtm: { path: "closed", trigger_and_target: "CTA" } }}
          >
            <Button
              className="body_16_m white"
              css={{
                height: "56px",
                width: "236px",
                background: colors.brown550,
                borderRadius: "8px",
              }}
            >
              장례 견적 알아보기
            </Button>
          </NextLink>
        </VStack>
      </ClosedSangjoLayout>
    </>
  );
};

export default ClosedSangjoPage;
